import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../SafeIcon';
import { supabase } from '@/integrations/supabase/client';
import demoScreenshot from '@/assets/demo-screenshot.png';

/**
 * Growth Plan §1.2 "P3 - Activation under 30s".
 * The new-user first-run flow. Two real steps:
 *   1. Pick market — personalizes the app.
 *   2. Live AI demo — auto-runs a fake analysis on a real sample chart
 *      (mock scanning progress, then verdict card appears). Zero user
 *      friction; the aha moment happens IN the onboarding itself, not
 *      after they land on /trading.
 * On finish we mark first_login=false and route to /trading with a
 * ?quickstart=1 query flag so the trading page can auto-focus the
 * capture button and show a first-time helper tooltip.
 */

const { FiArrowRight, FiZap, FiTarget, FiShield, FiCheckCircle, FiCpu } = FiIcons;

const MARKETS = [
  { id: 'gold',    label: 'Gold',    emoji: '🥇', desc: 'XAU/USD — institutional safe haven' },
  { id: 'crypto',  label: 'Crypto',  emoji: '₿',  desc: 'BTC, ETH, SOL & altcoins (24/7)' },
  { id: 'indices', label: 'Indices', emoji: '📊', desc: 'NAS100, US30, S&P 500, DAX' },
];

const SAMPLE = {
  decision: 'WAIT — CONDITIONS NOT MET',
  reason: 'Awaiting confirmation of breakout above resistance',
  invalidation: 'Below 4,135.00',
  confidence: 72,
  bullish: 40,
  bearish: 30,
  neutral: 30,
};

// Scanning simulation — must feel like a real institutional-grade pass
// (structure → momentum → risk) without adding real latency. Total time:
// SCAN_STEPS * SCAN_STEP_MS + reveal delay = ~6s.
const SCAN_STEPS = [
  'Reading price structure…',
  'Detecting support & resistance…',
  'Checking momentum divergence…',
  'Running risk-officer veto rules…',
  'Compiling verdict…',
];
const SCAN_STEP_MS = 900;

const OnboardingFlow = ({ userId, onComplete }) => {
  const [step, setStep] = useState(1);
  const [market, setMarket] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanIdx, setScanIdx] = useState(0);
  const [verdictReady, setVerdictReady] = useState(false);
  const navigate = useNavigate();

  // Auto-advance the scanning simulation on entering step 2.
  useEffect(() => {
    if (step !== 2) return;
    setScanIdx(0);
    setVerdictReady(false);
    let cancelled = false;
    let idx = 0;
    const tick = () => {
      if (cancelled) return;
      if (idx < SCAN_STEPS.length - 1) {
        idx += 1;
        setScanIdx(idx);
        setTimeout(tick, SCAN_STEP_MS);
      } else {
        setTimeout(() => !cancelled && setVerdictReady(true), 600);
      }
    };
    const t = setTimeout(tick, SCAN_STEP_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [step]);

  const finish = useCallback(async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ first_login: false })
        .eq('user_id', userId);
      if (error) console.error('[onboarding] mark first_login=false failed:', error.message);
    } catch (e) {
      console.error('[onboarding] update failed:', e);
    }
    setSaving(false);
    if (onComplete) onComplete();
    // ?quickstart=1 → /trading auto-focuses the capture control + shows
    // the first-time helper. Keeps the aha loop tight.
    navigate('/trading?quickstart=1');
  }, [userId, onComplete, navigate]);

  return (
    <div className="fixed inset-0 z-50 bg-gray-950/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl bg-gray-950 rounded-3xl border border-white/10 shadow-2xl p-6 sm:p-8 my-8 relative overflow-hidden"
      >
        {/* Subtle grid backdrop for terminal V3 aesthetic */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:32px_32px]" />

        <div className="relative">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-5">
            {[1, 2].map((n) => (
              <div key={n} className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: n <= step ? '100%' : '0%' }}
                  transition={{ duration: 0.4 }}
                  className="h-full bg-gradient-to-r from-cyan-400 to-purple-400"
                />
              </div>
            ))}
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
            ▸ Snap Chart AI · Step {step} of 2
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-black text-white mb-2 leading-tight">
                  What do you trade most?
                </h2>
                <p className="text-gray-400 mb-6 text-sm">
                  We&apos;ll personalize your first analysis. You can capture any market later.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                  {MARKETS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMarket(m.id)}
                      className={`p-5 rounded-xl border-2 text-left transition-all ${
                        market === m.id
                          ? 'border-cyan-400 bg-cyan-500/10 shadow-lg shadow-cyan-500/20'
                          : 'border-white/10 bg-black/40 hover:border-white/20'
                      }`}
                    >
                      <div className="text-3xl mb-2">{m.emoji}</div>
                      <div className="text-white font-semibold mb-1">{m.label}</div>
                      <div className="text-xs text-gray-400">{m.desc}</div>
                    </button>
                  ))}
                </div>
                <button
                  disabled={!market}
                  onClick={() => setStep(2)}
                  className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold shadow-lg shadow-cyan-500/30 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  See it in action <SafeIcon icon={FiArrowRight} />
                </button>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 text-center mt-4">
                  30 sec to your first verdict · no card required
                </p>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-black text-white mb-2 leading-tight">
                  {verdictReady ? 'Your AI risk officer.' : 'Analyzing a sample chart…'}
                </h2>
                <p className="text-gray-400 mb-5 text-sm">
                  {verdictReady
                    ? 'This is exactly what you get on every chart you capture. Your next analysis takes ~30 seconds.'
                    : 'Watch how it thinks. This is on a demo chart — yours next.'}
                </p>

                {/* Chart preview */}
                <div className="relative rounded-xl border border-white/10 overflow-hidden bg-black mb-4 aspect-[16/9]">
                  <img
                    src={demoScreenshot}
                    alt="Sample chart being analyzed"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      verdictReady ? 'opacity-40' : 'opacity-70'
                    }`}
                  />
                  {!verdictReady && (
                    <>
                      {/* Scanline animation */}
                      <motion.div
                        initial={{ y: 0 }}
                        animate={{ y: ['0%', '100%'] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                        className="absolute inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_16px_rgba(34,211,238,0.6)]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent pointer-events-none" />
                      {/* Scanning label */}
                      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-black/70 border border-cyan-500/30 backdrop-blur-sm">
                        <SafeIcon icon={FiCpu} className="w-3.5 h-3.5 text-cyan-400 animate-pulse flex-shrink-0" />
                        <span className="font-mono text-[11px] text-cyan-300 truncate">
                          {SCAN_STEPS[scanIdx]}
                        </span>
                      </div>
                    </>
                  )}
                  {verdictReady && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/20 border border-amber-500/40 backdrop-blur-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
                        Verdict ready
                      </span>
                    </div>
                  )}
                </div>

                {/* Verdict card — appears when scan completes */}
                <AnimatePresence>
                  {verdictReady && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-5 rounded-xl bg-black/60 border border-amber-500/30 mb-5"
                    >
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono text-amber-400 mb-2">
                        <SafeIcon icon={FiTarget} /> Quick summary
                      </div>
                      <div className="text-xl sm:text-2xl font-bold text-amber-400 mb-2">{SAMPLE.decision}</div>
                      <div className="text-sm text-gray-300 mb-4">{SAMPLE.reason}</div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="text-gray-500 uppercase tracking-widest font-mono mb-1 text-[10px]">Invalidation</div>
                          <div className="text-red-400 font-medium">{SAMPLE.invalidation}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 uppercase tracking-widest font-mono mb-1 text-[10px]">Confidence</div>
                          <div className="text-cyan-400 font-medium">{SAMPLE.confidence}%</div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <div className="text-gray-500 text-[10px] uppercase tracking-widest font-mono mb-2">Scenarios</div>
                        <div className="flex gap-2 text-xs">
                          <div className="flex-1 px-3 py-2 rounded-lg bg-green-500/10 text-green-400 text-center">Bullish · {SAMPLE.bullish}%</div>
                          <div className="flex-1 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-center">Bearish · {SAMPLE.bearish}%</div>
                          <div className="flex-1 px-3 py-2 rounded-lg bg-gray-700/40 text-gray-300 text-center">Wait · {SAMPLE.neutral}%</div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="px-5 py-3 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition-colors text-sm"
                  >
                    Back
                  </button>
                  <button
                    onClick={finish}
                    disabled={saving || !verdictReady}
                    className="group flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold shadow-lg shadow-cyan-500/30 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {saving
                      ? 'Starting…'
                      : verdictReady
                        ? <>Now capture <span className="hidden sm:inline">your</span> chart <SafeIcon icon={FiZap} /></>
                        : 'Analyzing…'}
                  </button>
                </div>
                {verdictReady && (
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 text-center mt-4">
                    ✓ Sample done in {(SCAN_STEPS.length * SCAN_STEP_MS + 600) / 1000}s · yours next
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingFlow;
